import type { RecommendationItem } from "@/types/api";

const CLOSED_STATUSES = new Set([
  "accepted",
  "dismissed",
  "rejected",
  "expired",
]);

const MATCH_TYPE_LABELS: Record<string, string> = {
  Exact: "Exact",
  Mapped: "Mapped",
  Upgrade: "Upgrade",
  NoMatch: "No match",
};

function statusKey(rec: RecommendationItem): string {
  return rec.status?.trim().toLowerCase() ?? "pending";
}

export function isOpenOpportunity(rec: RecommendationItem): boolean {
  return !CLOSED_STATUSES.has(statusKey(rec));
}

/**
 * Whether the acting user can accept / dismiss this opportunity: they must
 * hold `create:rebooks`, and the recommendation must be open (not already
 * accepted / dismissed) and not blocked by a restriction.
 */
export function canAcceptOpportunity(
  rec: Pick<RecommendationItem, "status" | "is_blocked">,
  canCreateRebooks: boolean,
): boolean {
  if (!canCreateRebooks) return false;
  if (rec.is_blocked) return false;
  return isOpenOpportunity(rec as RecommendationItem);
}

/**
 * Pick the alternatives to page through in the booking rail. Blocked
 * recommendations are omitted entirely. Open (pending) recs win; if a booking
 * only has closed recs we still show those so the action is never an empty
 * rail. Rank + savings decide order.
 */
export function selectBookingOpportunities(
  recs: RecommendationItem[],
): RecommendationItem[] {
  const unblocked = recs.filter((r) => !r.is_blocked);
  const open = unblocked.filter(isOpenOpportunity);
  const pool = open.length > 0 ? open : unblocked;
  return [...pool].sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    return b.savings - a.savings;
  });
}

export function matchTypeLabel(matchType: string): string {
  return MATCH_TYPE_LABELS[matchType] ?? matchType;
}

export function recStatusLabel(status?: string | null): string | null {
  const key = status?.trim().toLowerCase();
  if (!key || key === "pending") return null;
  return key.replace(/_/g, " ");
}

function quoteOnly(rec: Pick<RecommendationItem, "display_pricing">): boolean {
  return rec.display_pricing?.visibility === "quote";
}

function displayedQuotePrice(
  rec: Pick<RecommendationItem, "display_pricing" | "new_price">,
): number {
  return rec.display_pricing?.quote_price ?? rec.new_price;
}

/** Kai prompt for the booking-rail opportunity card. */
export function opportunityAskKaiPrompt(
  sourceId: string,
  rec: Pick<
    RecommendationItem,
    | "is_first_book"
    | "alternative_carrier"
    | "savings"
    | "new_price"
    | "display_pricing"
  >,
): string {
  const carrier =
    rec.alternative_carrier ?? (rec.is_first_book ? "quoted" : "alternative");
  if (rec.is_first_book) {
    return (
      `Review the first-book quote for booking ${sourceId}. ` +
      `Highlight the ${carrier} option` +
      (quoteOnly(rec)
        ? "."
        : ` at ${Math.round(displayedQuotePrice(rec)).toLocaleString()} USD.`)
    );
  }
  return (
    `Review the rebook opportunities for booking ${sourceId}. ` +
    `Highlight the ${carrier} option` +
    (quoteOnly(rec)
      ? "."
      : ` that saves ${Math.round(rec.savings).toLocaleString()} USD.`)
  );
}
